import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../axios";
import { toast } from "sonner";

/**
 * Minimal Usuario shape needed by these hooks.
 * Each frontend's full Usuario type should extend or satisfy this.
 */
export interface UsuarioBase {
  id: number;
  rol?: unknown;
}

export type UpdateUsuarioPayload = {
  id: number;
  data: Partial<Omit<UsuarioBase, "id" | "rol">>;
};

interface UseUpdateUsuarioProps {
  queryKey: string[];
}

export const useUsuarioInfo = <T extends UsuarioBase = UsuarioBase>(
  id: number,
) => {
  return useQuery<T, Error>({
    queryKey: ["usuarioInfo", id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/usuarios/${id}`);
      return response.data;
    },
  });
};

export const useUpdateUsuario = ({ queryKey }: UseUpdateUsuarioProps) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateUsuarioPayload) => {
      const res = await axiosInstance.patch(`/usuarios/${id}`, data, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Usuario actualizado correctamente.");
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.error("Error al actualizar usuario.");
    },
  });
};
